import React, { useState, useEffect, useRef } from 'react';
import { Database, Trash2, Archive, Download, RefreshCw, ChevronLeft, AlertTriangle, CheckCircle, CheckCircle2, Clock, HardDrive, Upload, RotateCcw, CalendarClock, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, formatRelativeTime } from '../utils/dateFormatter';

const AdminUtilities = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [backups, setBackups] = useState([]);
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const uploadInputRef = useRef(null);

  // Auto-backup schedule state
  const [autoBackup, setAutoBackup] = useState(null);
  const [autoBackupHour, setAutoBackupHour] = useState('3');
  const [autoBackupRetention, setAutoBackupRetention] = useState('14');
  const [savingAutoBackup, setSavingAutoBackup] = useState(false);
  const [autoBackupMessage, setAutoBackupMessage] = useState(null);

  useEffect(() => {
    checkAdminAccess();
    fetchData();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const user = await response.json();
        if (!user.is_admin) {
          navigate('/');
        }
      } else {
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      navigate('/admin/login');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchOrphans(),
        fetchBackups(),
        fetchArchivedUsers(),
        fetchAutoBackup(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoBackup = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/auto-backup`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAutoBackup(data);
        setAutoBackupHour(String(data.hour_utc ?? 3));
        setAutoBackupRetention(String(data.retention_days ?? 14));
      }
    } catch (error) {
      console.error('Failed to fetch auto-backup config:', error);
    }
  };

  const handleSaveAutoBackup = async (overrides = {}) => {
    setSavingAutoBackup(true);
    setAutoBackupMessage(null);
    try {
      const body = {
        hour_utc: parseInt(autoBackupHour, 10),
        retention_days: parseInt(autoBackupRetention, 10),
        ...overrides,
      };
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/auto-backup`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: JSON.stringify(body),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAutoBackup(data);
        setAutoBackupMessage({ type: 'success', text: '✅ Schedule updated.' });
      } else {
        setAutoBackupMessage({ type: 'error', text: 'Failed to update schedule.' });
      }
    } catch (error) {
      console.error('Failed to save auto-backup:', error);
      setAutoBackupMessage({ type: 'error', text: 'Failed to update schedule.' });
    } finally {
      setSavingAutoBackup(false);
    }
  };

  const handleRunAutoBackupNow = async () => {
    setSavingAutoBackup(true);
    setAutoBackupMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/auto-backup/run-now`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && !data.last_error) {
        setAutoBackupMessage({
          type: 'success',
          text: `✅ Ran now: ${data.last_filename} (${data.last_total_records} records).`,
        });
        await fetchAutoBackup();
        await fetchBackups();
      } else {
        setAutoBackupMessage({
          type: 'error',
          text: data.last_error || data.detail || 'Run failed.',
        });
      }
    } catch (error) {
      console.error('Failed to run now:', error);
      setAutoBackupMessage({ type: 'error', text: 'Run failed.' });
    } finally {
      setSavingAutoBackup(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/stats`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchOrphans = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/orphans`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setOrphans(data);
      }
    } catch (error) {
      console.error('Failed to fetch orphans:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/backups`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups);
      }
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    }
  };

  const fetchArchivedUsers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/archived`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setArchivedUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to fetch archived users:', error);
    }
  };

  const handleCleanOrphans = async () => {
    if (!window.confirm(`⚠️ WARNING: This will permanently delete ${orphans.total} orphaned records.\n\nThis action CANNOT be undone.\n\nAre you sure?`)) {
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/clean-orphans`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchData();
      } else {
        setMessage({ type: 'error', text: 'Failed to clean orphaned records' });
      }
    } catch (error) {
      console.error('Failed to clean orphans:', error);
      setMessage({ type: 'error', text: 'Failed to clean orphaned records' });
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a backup of the entire database?\n\nThis may take a few moments.')) {
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: 'Creating backup... Please wait.' });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/backup`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ 
          type: 'success', 
          text: `✅ Backup created: ${data.filename}\nTotal records: ${data.total_records}` 
        });
        fetchBackups();
      } else {
        setMessage({ type: 'error', text: 'Failed to create backup' });
      }
    } catch (error) {
      console.error('Failed to create backup:', error);
      setMessage({ type: 'error', text: 'Failed to create backup' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadBackup = (filename) => {
    // Uses a query-string token so the browser can trigger its native download dialog
    const token = localStorage.getItem('session_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/backup/download/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
    // Open in a new tab so the browser streams the file to disk
    window.open(url, '_blank');
  };

  const handleRestoreBackup = async (filename) => {
    const confirmText = window.prompt(
      `⚠️  DANGER: Restoring "${filename}" in REPLACE mode will WIPE all current data in these collections:\n` +
        ` • users, sessions, reflections, insights, action plans, reviews, settings\n\n` +
        `and replace them with the contents of this backup.\n\n` +
        `This action CANNOT be undone.\n\n` +
        `Type "RESTORE" (in capital letters) to confirm, or press Cancel.`
    );
    if (confirmText !== 'RESTORE') {
      if (confirmText !== null) {
        setMessage({ type: 'info', text: 'Restore cancelled (confirmation text did not match).' });
      }
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: 'Restoring… please do not close this tab.' });
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/restore/${encodeURIComponent(filename)}?mode=replace`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const summary = Object.entries(data.restored || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        setMessage({
          type: 'success',
          text: `✅ Restored from ${data.filename} (${data.total_records} records)\n${summary}`,
        });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Restore failed' });
      }
    } catch (error) {
      console.error('Failed to restore backup:', error);
      setMessage({ type: 'error', text: 'Restore failed' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"?\n\nThis cannot be undone.`)) return;
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/backup/${encodeURIComponent(filename)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage({ type: 'success', text: data.message || 'Backup deleted' });
        fetchBackups();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to delete backup' });
      }
    } catch (error) {
      console.error('Failed to delete backup:', error);
      setMessage({ type: 'error', text: 'Failed to delete backup' });
    } finally {
      setProcessing(false);
    }
  };

  const handleUploadRestore = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // reset so selecting the same file twice works
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setMessage({ type: 'error', text: 'Please choose a .json backup file.' });
      return;
    }

    const confirmText = window.prompt(
      `⚠️  Upload & RESTORE from "${file.name}"?\n\n` +
        `This will WIPE all current data in the application collections and replace them with the uploaded file's contents.\n\n` +
        `This action CANNOT be undone.\n\n` +
        `Type "RESTORE" to confirm, or press Cancel.`
    );
    if (confirmText !== 'RESTORE') {
      if (confirmText !== null) {
        setMessage({ type: 'info', text: 'Upload-restore cancelled (confirmation text did not match).' });
      }
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: `Uploading "${file.name}" and restoring… please wait.` });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'replace');
      formData.append('save_copy', 'true');

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/restore-upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: formData,
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const summary = Object.entries(data.restored || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        setMessage({
          type: 'success',
          text: `✅ Restored from upload ${file.name}${data.saved_as ? ' (saved as ' + data.saved_as + ')' : ''}\n${data.total_records} records\n${summary}`,
        });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Upload-restore failed' });
      }
    } catch (error) {
      console.error('Failed to upload-restore:', error);
      setMessage({ type: 'error', text: 'Upload-restore failed' });
    } finally {
      setProcessing(false);
    }
  };

  const handleCleanExpiredSessions = async () => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/database/clean-expired-sessions`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchStats();
      } else {
        setMessage({ type: 'error', text: 'Failed to clean sessions' });
      }
    } catch (error) {
      console.error('Failed to clean sessions:', error);
      setMessage({ type: 'error', text: 'Failed to clean sessions' });
    } finally {
      setProcessing(false);
    }
  };

  const handleUnarchiveUser = async (userId) => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/unarchive`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('session_token')}`,
          },
        }
      );
      
      if (response.ok) {
        setMessage({ type: 'success', text: 'User unarchived successfully' });
        fetchArchivedUsers();
      } else {
        setMessage({ type: 'error', text: 'Failed to unarchive user' });
      }
    } catch (error) {
      console.error('Failed to unarchive user:', error);
      setMessage({ type: 'error', text: 'Failed to unarchive user' });
    } finally {
      setProcessing(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-brand-bg">
      {/* Header */}
      <div className="bg-brand-accent text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-brand-accent rounded-lg transition-colors"
                title="Back to Dashboard"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Database Utilities</h1>
                <p className="text-white/80 mt-1">Maintenance, backup, and cleanup tools</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={processing}
              className="flex items-center space-x-2 px-3 py-2 bg-brand-accent hover:bg-brand-accent-hover rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-5 w-5 ${processing ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border ${
            message.type === 'success' ? 'bg-green-50 border-green-200' :
            message.type === 'error' ? 'bg-red-50 border-red-200' :
            'bg-blue-50 border-blue-200'
          }`}>
            <p className={`whitespace-pre-wrap ${
              message.type === 'success' ? 'text-green-900' :
              message.type === 'error' ? 'text-red-900' :
              'text-blue-900'
            }`}>
              {message.text}
            </p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Database Statistics */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Database className="h-6 w-6 mr-2 text-brand-accent" />
                Database Statistics
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{stats?.users || 0}</p>
                  <p className="text-sm text-gray-600">Users</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{stats?.reflections || 0}</p>
                  <p className="text-sm text-gray-600">Reflections</p>
                </div>
                <div className="text-center p-4 bg-brand-accent/5 rounded-lg">
                  <p className="text-2xl font-bold text-brand-accent">{stats?.insights || 0}</p>
                  <p className="text-sm text-gray-600">Insights</p>
                </div>
                <div className="text-center p-4 bg-brand-accent/5 rounded-lg">
                  <p className="text-2xl font-bold text-brand-accent">{stats?.action_plans || 0}</p>
                  <p className="text-sm text-gray-600">Action Plans</p>
                </div>
                <div className="text-center p-4 bg-pink-50 rounded-lg">
                  <p className="text-2xl font-bold text-pink-600">{stats?.reviews || 0}</p>
                  <p className="text-sm text-gray-600">Reviews</p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-600">{stats?.sessions || 0}</p>
                  <p className="text-sm text-gray-600">Sessions</p>
                </div>
              </div>
            </div>

            {/* Orphaned Records */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <AlertTriangle className="h-6 w-6 mr-2 text-orange-600" />
                  Orphaned Records
                </h2>
                {orphans && orphans.total > 0 && (
                  <button
                    onClick={handleCleanOrphans}
                    disabled={processing}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Clean All Orphans</span>
                  </button>
                )}
              </div>
              
              {orphans && orphans.total > 0 ? (
                <>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-orange-800">
                      <strong>⚠️ Found {orphans.total} orphaned records</strong> - Data without associated users.
                      This can happen when users are deleted but their data remains.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{orphans.reflections}</p>
                      <p className="text-sm text-gray-600">Reflections</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{orphans.insights}</p>
                      <p className="text-sm text-gray-600">Insights</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{orphans.action_plans}</p>
                      <p className="text-sm text-gray-600">Action Plans</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{orphans.reviews}</p>
                      <p className="text-sm text-gray-600">Reviews</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{orphans.sessions}</p>
                      <p className="text-sm text-gray-600">Sessions</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">✅ No orphaned records found. Database is clean!</p>
                </div>
              )}
            </div>

            {/* Automated Backup Schedule */}
            <div
              data-testid="auto-backup-card"
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-6"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <CalendarClock className="h-6 w-6 mr-2 text-brand-accent" />
                  Automated Daily Backups
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">
                    {autoBackup?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button
                    data-testid="auto-backup-toggle"
                    onClick={() =>
                      handleSaveAutoBackup({ enabled: !autoBackup?.enabled })
                    }
                    disabled={savingAutoBackup}
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                      autoBackup?.enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                    aria-pressed={autoBackup?.enabled ? 'true' : 'false'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        autoBackup?.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                A full backup of every application collection is written to{' '}
                <code className="bg-gray-100 px-1 rounded">/app/backend/backups</code>
                {' '}every day at the scheduled UTC hour. Files older than the retention
                window are pruned automatically.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Run daily at (UTC hour)
                  </label>
                  <select
                    data-testid="auto-backup-hour"
                    value={autoBackupHour}
                    onChange={(e) => setAutoBackupHour(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  >
                    {Array.from({ length: 24 }).map((_, h) => (
                      <option key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00 UTC
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Keep auto-backups for (days)
                  </label>
                  <input
                    data-testid="auto-backup-retention"
                    type="number"
                    min="1"
                    max="365"
                    value={autoBackupRetention}
                    onChange={(e) => setAutoBackupRetention(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <button
                    data-testid="auto-backup-save"
                    onClick={() => handleSaveAutoBackup()}
                    disabled={savingAutoBackup}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-accent hover:bg-brand-accent text-white rounded-lg font-semibold disabled:opacity-50"
                  >
                    {savingAutoBackup ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" /> Saving…
                      </>
                    ) : (
                      'Save schedule'
                    )}
                  </button>
                  <button
                    data-testid="auto-backup-run-now"
                    onClick={handleRunAutoBackupNow}
                    disabled={savingAutoBackup}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-brand-border text-brand-accent hover:bg-brand-accent/5 rounded-lg font-semibold disabled:opacity-50"
                    title="Run an auto-backup right now"
                  >
                    <Play className="h-4 w-4" /> Run now
                  </button>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Next scheduled
                  </p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {autoBackup?.enabled
                      ? autoBackup?.next_run_at
                        ? formatDateTime(autoBackup.next_run_at)
                        : '—'
                      : 'Paused'}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Last successful run
                  </p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {autoBackup?.last_run_at
                      ? `${formatDateTime(autoBackup.last_run_at)} (${formatRelativeTime(autoBackup.last_run_at)})`
                      : 'Never'}
                  </p>
                  {autoBackup?.last_filename && (
                    <p className="font-mono text-xs text-gray-500 mt-1 truncate">
                      {autoBackup.last_filename} · {autoBackup.last_total_records} records
                    </p>
                  )}
                </div>
                <div
                  className={`p-3 rounded-lg ${
                    autoBackup?.last_error
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-green-50 border border-green-200'
                  }`}
                >
                  <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                  <p
                    className={`font-semibold mt-1 ${
                      autoBackup?.last_error ? 'text-red-700' : 'text-green-700'
                    }`}
                  >
                    {autoBackup?.last_error ? 'Last run failed' : 'Healthy'}
                  </p>
                  {autoBackup?.last_error && (
                    <p className="text-xs text-red-600 mt-1 break-words">
                      {autoBackup.last_error}
                    </p>
                  )}
                </div>
              </div>

              {autoBackupMessage && (
                <div
                  data-testid="auto-backup-message"
                  className={`mt-4 p-3 rounded-lg text-sm ${
                    autoBackupMessage.type === 'success'
                      ? 'bg-green-100 text-green-900 border border-green-200'
                      : 'bg-red-100 text-red-900 border border-red-200'
                  }`}
                >
                  {autoBackupMessage.text}
                </div>
              )}
            </div>

            {/* Backup & Restore */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8" data-testid="backup-restore-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <HardDrive className="h-6 w-6 mr-2 text-brand-accent" />
                  Backups & Restore
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="file"
                    accept="application/json,.json"
                    ref={uploadInputRef}
                    onChange={handleUploadRestore}
                    className="hidden"
                    data-testid="backup-upload-input"
                  />
                  <button
                    onClick={() => uploadInputRef.current?.click()}
                    disabled={processing}
                    className="flex items-center space-x-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors disabled:opacity-50"
                    data-testid="upload-restore-btn"
                    title="Upload a backup file from your computer and restore from it"
                  >
                    <Upload className="h-4 w-4" />
                    <span>Upload & Restore</span>
                  </button>
                  <button
                    onClick={handleCreateBackup}
                    disabled={processing}
                    className="flex items-center space-x-2 px-4 py-2 bg-brand-accent hover:bg-brand-accent text-white rounded-lg transition-colors disabled:opacity-50"
                    data-testid="create-backup-btn"
                  >
                    <Download className="h-4 w-4" />
                    <span>Create Backup</span>
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-brand-ink">
                <strong>Tip:</strong> Download a backup to keep an offline copy. Restore will <strong>wipe</strong> current data in the restored collections and replace them with the backup contents — always download a fresh backup first.
              </div>

              {backups.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filename</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kind</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {backups.map((backup, index) => (
                        <tr key={index} className="hover:bg-gray-50" data-testid={`backup-row-${backup.filename}`}>
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 break-all">{backup.filename}</td>
                          <td className="px-4 py-3 text-xs whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                              backup.kind === 'auto'
                                ? 'bg-brand-accent/10 text-brand-accent'
                                : backup.kind === 'upload'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {backup.kind || 'manual'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatBytes(backup.size)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatDateTime(backup.created)}
                            <span className="text-xs text-gray-500 ml-2">({formatRelativeTime(backup.created)})</span>
                          </td>
                          <td className="px-4 py-3 text-xs whitespace-nowrap" title={backup.storage_uploaded ? 'Saved to object storage — survives redeploys' : 'Local-only — will be lost on redeploy'}>
                            {backup.storage_uploaded ? (
                              <span className="inline-flex items-center gap-1 text-green-700">
                                <CheckCircle2 className="h-3.5 w-3.5" /> Cloud
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <AlertTriangle className="h-3.5 w-3.5" /> Local only
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => handleDownloadBackup(backup.filename)}
                                disabled={processing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-brand-accent bg-brand-accent/5 hover:bg-brand-accent/10 rounded-md disabled:opacity-50"
                                data-testid={`download-backup-${backup.filename}`}
                                title="Download to your computer"
                              >
                                <Download className="h-4 w-4" /> Download
                              </button>
                              <button
                                onClick={() => handleRestoreBackup(backup.filename)}
                                disabled={processing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-brand-cta bg-amber-50 hover:bg-amber-100 rounded-md disabled:opacity-50"
                                data-testid={`restore-backup-${backup.filename}`}
                                title="Restore database from this backup"
                              >
                                <RotateCcw className="h-4 w-4" /> Restore
                              </button>
                              <button
                                onClick={() => handleDeleteBackup(backup.filename)}
                                disabled={processing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-red-700 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50"
                                data-testid={`delete-backup-${backup.filename}`}
                                title="Delete this backup file"
                              >
                                <Trash2 className="h-4 w-4" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <HardDrive className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No backups yet. Click <strong>Create Backup</strong> or <strong>Upload &amp; Restore</strong> to get started.</p>
                </div>
              )}
            </div>

            {/* Archived Users */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Archive className="h-6 w-6 mr-2 text-brand-accent" />
                Archived Users ({archivedUsers.length})
              </h2>

              {archivedUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Archived</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {archivedUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{user.name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {user.archived_at ? formatDateTime(user.archived_at) : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleUnarchiveUser(user.id)}
                              disabled={processing}
                              className="text-sm text-brand-accent hover:text-brand-accent disabled:opacity-50"
                            >
                              Unarchive
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Archive className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No archived users</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Clock className="h-6 w-6 mr-2 text-green-600" />
                Quick Actions
              </h2>
              <div className="space-y-3">
                <button
                  onClick={handleCleanExpiredSessions}
                  disabled={processing}
                  className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Clean Expired Sessions</p>
                      <p className="text-sm text-gray-600">Remove old user sessions from database</p>
                    </div>
                  </div>
                  <span className="text-sm text-green-600 font-medium">Run</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUtilities;
